import React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";

// Root component props
interface ToggleGroupSingleProps {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface ToggleGroupMultipleProps {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

type ToggleGroupProps = ToggleGroupSingleProps | ToggleGroupMultipleProps;

// Item component props
interface ToggleGroupItemProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  "aria-label"?: string;
  title?: string;
}

/**
 * ToggleGroup component wrapping Radix UI ToggleGroup primitive.
 * Used for single or multiple selection from a group of options.
 *
 * Visual difference from individual Toggle: ToggleGroup manages selection
 * state across multiple related items with keyboard navigation support.
 *
 * Supports both single and multiple selection modes.
 * Supports both controlled (value) and uncontrolled (defaultValue) modes.
 */
const ToggleGroupRoot = React.forwardRef<HTMLDivElement, ToggleGroupProps>(
  (props, forwardedRef) => {
    if (props.type === "single") {
      return (
        <ToggleGroupPrimitive.ToggleGroup
          ref={forwardedRef}
          type="single"
          value={props.value}
          defaultValue={props.defaultValue}
          onValueChange={props.onValueChange}
          disabled={props.disabled}
          className={`flex gap-1 ${props.className || ""}`}
        >
          {props.children}
        </ToggleGroupPrimitive.ToggleGroup>
      );
    }

    return (
      <ToggleGroupPrimitive.ToggleGroup
        ref={forwardedRef}
        type="multiple"
        value={props.value}
        defaultValue={props.defaultValue}
        onValueChange={props.onValueChange}
        disabled={props.disabled}
        className={`flex gap-1 ${props.className || ""}`}
      >
        {props.children}
      </ToggleGroupPrimitive.ToggleGroup>
    );
  }
);

ToggleGroupRoot.displayName = "ToggleGroup";

const ToggleGroupItem = React.forwardRef<
  HTMLButtonElement,
  ToggleGroupItemProps
>(({ value, disabled, className, children, ...props }, forwardedRef) => {
  return (
    <ToggleGroupPrimitive.Item
      ref={forwardedRef}
      value={value}
      disabled={disabled}
      className={`inline-flex items-center justify-center px-4 py-2 rounded border-4 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-950 data-[state=on]:border-green-500 data-[state=off]:border-gray-800 hover:border-purple-500 *:text-sm *:sm:text-base ${
        className || ""
      }`}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = "ToggleGroupItem";

// Compound component export
export const ToggleGroup = Object.assign(ToggleGroupRoot, {
  Item: ToggleGroupItem,
});

export default ToggleGroup;
